import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useHistory } from 'react-router-dom'

import _ from 'lodash'
import queryString from 'query-string'
import {
  changeFilterGroups,
  clearFilters,
  setActiveFilterId,
  setDefaultFilterId,
  setSavedFilters
} from 'slices/filters'

import {
  assignAdminDefaultOrgFilterSettings,
  createSavedFilter,
  deleteSavedFilter,
  getSavedFilters,
  patchSavedFilter,
  removeDefaultFilter,
  setDefaultFilter,
  toggleDefaultFilter
} from 'api/AccountActivityAPI'
import { DeserializedSavedFilter, INewSavedFilter, SavedFilter } from 'api/AccountActivityAPI/types'
import { CurrentUserContext } from 'context/currentUserContext'
import useNotificationsUrlParams from 'hooks/useNotificationsUrlParams'
import useQueryParams from 'hooks/useQueryParams'
import {
  FilterGroup,
  FilterPage,
  getActivityFiltersFromQueryString,
  serializeFilters
} from 'utils/filters'
import { captureExceptionWithMessage } from 'utils/logRocket'
import { transformFiltersToFrontend } from 'utils/serializeFilters'
import { segmentTrack, TrackType } from 'utils/tracking'

import { fsTrackEvent, FULLSTORY_CUSTOM_EVENT } from './../utils/fullstory'
import useNotifications from './useNotifications'
import { useAppDispatch, useAppSelector } from './useRedux'

export const INACTIVE_FILTER = 0

export default function useSavedFilters() {
  const history = useHistory()
  const queryParams = useQueryParams()
  const { success } = useNotifications()
  const dispatch = useAppDispatch()
  const {
    filterGroupsByPage,
    savedFilters,
    defaultFilterId,
    activeFilterId,
    loaded: filtersLoaded,
    sharedFilters,
    privateFilters
  } = useAppSelector(({ filters }) => filters)
  const onChangeFilterGroups = (filterGroups: FilterGroup[]) =>
    dispatch(changeFilterGroups({ filterGroups }))

  const onClearFilterGroups = () => dispatch(clearFilters({}))

  const activeSavedFilter: DeserializedSavedFilter | undefined = useMemo(() => {
    return savedFilters?.find(filter => filter.id === activeFilterId)
  }, [savedFilters, activeFilterId])

  const currentUser = useContext(CurrentUserContext)
  const orgUsersById = useAppSelector(({ orgUsersById }) => orgUsersById.users)

  useEffect(() => {
    if (activeSavedFilter && filtersLoaded) {
      onChangeFilterGroups(activeSavedFilter.body.filterGroups)
    }
  }, [activeSavedFilter, filtersLoaded]) // eslint-disable-line

  const updateActiveFilterId = (newFilterId: number | undefined) => {
    if (!newFilterId) {
      newFilterId = INACTIVE_FILTER
    }
    if (newFilterId === INACTIVE_FILTER && activeSavedFilter?.id) {
      onClearFilterGroups()
    }
    dispatch(setActiveFilterId(newFilterId))
  }

  const parseQueryParams = (
    defaultFilterId: number | undefined,
    savedFilters: DeserializedSavedFilter[] | SavedFilter[] | any[]
  ) => {
    if (queryParams.has('savedFilterId')) {
      const queryId = parseInt(queryParams.get('savedFilterId') || '')
      const filters = queryParams.get('filters' || '')
      const savedFilterId = !isNaN(queryId) ? queryId : INACTIVE_FILTER
      if (savedFilterId === INACTIVE_FILTER) {
        dispatch(setActiveFilterId(INACTIVE_FILTER))
        if (filters) {
          const json = getActivityFiltersFromQueryString() as FilterGroup[]
          if (json) {
            dispatch(changeFilterGroups({ filterGroups: json, page: FilterPage.ACTIVITIES }))
          }
        }
      } else {
        dispatch(setActiveFilterId(savedFilterId))
      }
    } else if (defaultFilterId) {
      dispatch(setActiveFilterId(defaultFilterId))
    } else {
      dispatch(setActiveFilterId(INACTIVE_FILTER))
    }

    dispatch(setSavedFilters(savedFilters))
  }

  const createNewSavedFilter = async (newFilter: INewSavedFilter, isDefault: boolean) => {
    try {
      const { data: filterId } = await createSavedFilter(newFilter)
      isDefault && (await setDefaultFilter(filterId))
      segmentTrack(TrackType.SAVED_FILTER_CREATED)

      const createdFilter = { id: filterId, ...newFilter }
      fsTrackEvent(FULLSTORY_CUSTOM_EVENT.SAVED_FILTER_CREATED)

      if (savedFilters) {
        dispatch(setSavedFilters([...savedFilters, createdFilter]))
      }
      isDefault && dispatch(setDefaultFilterId(filterId))
      dispatch(setActiveFilterId(filterId))

      if (newFilter?.orgSettings) {
        const { orgSettings } = newFilter
        await assignAdminDefaultOrgFilterSettings(filterId, orgSettings)
      }

      success(`Saved "${createdFilter.name}"`)
    } catch (error) {
      captureExceptionWithMessage('Error creating saved filter', error, {
        url: window.location.href,
        userId: `${currentUser?.id}`
      })
    }
  }

  const updateSavedFilter = async (updatedFilter: DeserializedSavedFilter, isDefault: boolean) => {
    try {
      await patchSavedFilter(updatedFilter.id, updatedFilter)
      await toggleDefaultFilter(updatedFilter.id, isDefault)
      if (updatedFilter?.orgSettings) {
        const { orgSettings } = updatedFilter
        await assignAdminDefaultOrgFilterSettings(updatedFilter.id, orgSettings)
      }
      segmentTrack(TrackType.SAVED_FILTER_UPDATED)
    } catch (error) {
      captureExceptionWithMessage('Error updating saved filter', error, {
        url: window.location.href,
        userId: `${currentUser?.id}`
      })
    }
    const filters = savedFilters?.map(filter =>
      filter.id === updatedFilter.id ? updatedFilter : filter
    )
    dispatch(setSavedFilters(filters))
    isDefault && dispatch(setDefaultFilterId(updatedFilter.id))
    dispatch(setActiveFilterId(updatedFilter.id))

    success(`Saved changes to "${updatedFilter.name}"`)
  }

  const updateDefaultFilterId = async (filterId: number) => {
    setDefaultFilter(filterId).then(() => {
      dispatch(setDefaultFilterId(filterId))
      success(`Successfully set as your default saved filters`)
    })
  }

  const deleteDefaultFilter = async () => {
    try {
      await removeDefaultFilter()
      dispatch(setDefaultFilterId(0))
      success(`Successfully removed your default saved filter`)
    } catch (error) {
      captureExceptionWithMessage('Error deleting default filter', error, {
        url: window.location.href,
        userId: `${currentUser?.id}`
      })
    }
  }

  const removeSavedFilter = async (filterId: number) => {
    try {
      if (filterId) {
        await deleteSavedFilter(filterId)
        segmentTrack(TrackType.SAVED_FILTER_DELETED)
      }
    } catch (error) {
      captureExceptionWithMessage('Error deleting saved filter', error, {
        url: window.location.href,
        userId: `${currentUser?.id}`
      })
    }

    const removedFilter = savedFilters?.find(filter => filter.id === filterId)
    const updatedFilters = savedFilters?.filter(filter => filter.id !== filterId)
    dispatch(setSavedFilters(updatedFilters))
    dispatch(setActiveFilterId(INACTIVE_FILTER))

    success(`Removed "${removedFilter?.name}"`)
  }

  const fetchSavedFilters = useCallback(async () => {
    try {
      const { defaultFilterId, savedFilterSets } = await getSavedFilters()
      const savedFilters = transformFiltersToFrontend(savedFilterSets, orgUsersById)

      return { defaultFilterId, savedFilters }
    } catch (error) {
      captureExceptionWithMessage('Error fetching saved filter', error, {
        url: window.location.href,
        userId: `${currentUser?.id}`
      })
    }
  }, [orgUsersById, currentUser])

  useNotificationsUrlParams(fetchSavedFilters)

  useEffect(() => {
    if (activeFilterId === undefined) {
      return
    }

    const json = serializeFilters({
      ACCOUNTS: [],
      ACTIVITIES: filterGroupsByPage.ACTIVITIES
    })
    const base64 = btoa(encodeURIComponent(json)).replace(/=/g, '')

    const search = queryString.stringify({
      savedFilterId: activeFilterId,
      filters: activeFilterId === INACTIVE_FILTER ? base64 : undefined
    })

    history.replace({
      search,
      hash: history.location.hash
    })
  }, [history, activeFilterId, filterGroupsByPage])

  useEffect(() => {
    const onInitialLoad = async () => {
      try {
        const data = await fetchSavedFilters()
        if (data) {
          const { defaultFilterId, savedFilters } = data

          dispatch(setDefaultFilterId(defaultFilterId))
          parseQueryParams(defaultFilterId, savedFilters)
        }
      } catch (error) {
        captureExceptionWithMessage('Error loading saved filters', error, {
          url: window.location.href,
          userId: `${currentUser?.id}`
        })
      }
    }

    if (!_.isEmpty(orgUsersById) && !filtersLoaded) {
      onInitialLoad()
    }
  }, [orgUsersById, filtersLoaded]) // eslint-disable-line

  return {
    activeSavedFilter,
    defaultFilterId,
    savedFilters,
    updateActiveFilterId,
    updateDefaultFilterId,
    createNewSavedFilter,
    updateSavedFilter,
    removeSavedFilter,
    sharedFilters,
    privateFilters,
    filtersLoaded,
    deleteDefaultFilter
  }
}
